// Copyright 2026 Bitech.id. Licensed under the MIT License.

import { BiwaveClient, setCurrentClient } from '@biwave/core';
import type { BiwaveEvent } from '@biwave/core';
import pkg from '../package.json' with { type: 'json' };
import { setBrowserContext, setDeviceContext, setPageContext } from './context.js';
import { installConsoleBreadcrumbs } from './integrations/console-breadcrumbs.js';
import { installFetchBreadcrumbs } from './integrations/fetch-breadcrumbs.js';
import { installLogFlushOnUnload } from './integrations/log-flush.js';
import { installNavigationBreadcrumbs } from './integrations/navigation-breadcrumbs.js';
import { installUnhandledRejectionHandler } from './integrations/unhandled-rejection.js';
import { installWindowErrorHandler } from './integrations/window-error.js';
import { getReplaySessionId, notifyErrorCaptured } from './stream/session-ref.js';
import type { StreamOptions } from './stream/types.js';
import { DEFAULT_STREAM_OPTIONS } from './stream/types.js';

export type { BiwaveClient, UserInfo } from '@biwave/core';
export type { StreamOptions } from './stream/types.js';
/** Ends the active Biwave Stream session early, e.g. on user consent withdrawal. A no-op if Stream isn't active. */
export { requestStreamStop } from './stream/session-ref.js';

export interface BiwaveBrowserOptions {
  clientId: string;
  endpoint?: string;
  environment?: string;
  release?: string;
  dist?: string;
  maxBreadcrumbs?: number;
  logBatchSize?: number;
  logFlushInterval?: number;
  logMaxMemoryEvents?: number;
  pulseBatchSize?: number;
  pulseFlushInterval?: number;
  pulseMaxMemoryEvents?: number;
  beforeSend?: (event: BiwaveEvent) => BiwaveEvent | null;
  /** Biwave Stream (session replay) — off by default. Lazy-loaded: apps that don't enable this never load rrweb. */
  stream?: StreamOptions;
}

export function init(options: BiwaveBrowserOptions): BiwaveClient {
  const client = new BiwaveClient({
    clientId: options.clientId,
    endpoint: options.endpoint,
    platform: 'browser',
    sdk: { name: '@biwave/browser', version: pkg.version },
    environment: options.environment,
    release: options.release,
    dist: options.dist,
    maxBreadcrumbs: options.maxBreadcrumbs,
    logBatchSize: options.logBatchSize,
    logFlushInterval: options.logFlushInterval,
    logMaxMemoryEvents: options.logMaxMemoryEvents,
    pulseBatchSize: options.pulseBatchSize,
    pulseFlushInterval: options.pulseFlushInterval,
    pulseMaxMemoryEvents: options.pulseMaxMemoryEvents,
    beforeSend: (event) => {
      // Correlates this Trace event with the active Stream session, if any.
      // No dedicated field on BiwaveEvent yet — tags.session_id is the
      // agreed interim slot (see docs/docs.md's Stream section).
      const sessionId = getReplaySessionId();
      if (sessionId) {
        event.tags = { ...event.tags, session_id: sessionId };
      }
      notifyErrorCaptured();
      return options.beforeSend ? options.beforeSend(event) : event;
    },
  });

  setBrowserContext(client);
  setPageContext(client);
  setDeviceContext(client);
  setCurrentClient(client);

  installWindowErrorHandler(client);
  installUnhandledRejectionHandler(client);
  installConsoleBreadcrumbs(client);
  installFetchBreadcrumbs(client);
  installNavigationBreadcrumbs(client);
  installLogFlushOnUnload(client);

  if (options.stream?.enabled) {
    initStream(client, options).catch((err: unknown) => {
      console.debug('[Biwave Stream] failed to load', err);
    });
  }

  return client;
}

async function initStream(client: BiwaveClient, options: BiwaveBrowserOptions): Promise<void> {
  const { StreamController } = await import('./stream/stream.js');
  const resolved = { ...DEFAULT_STREAM_OPTIONS, ...options.stream };

  new StreamController({
    client,
    clientId: options.clientId,
    endpoint: client.target.endpoint,
    environment: options.environment,
    sdkVersion: pkg.version,
    options: resolved,
  }).start();
}
